import React, { useState } from 'react';
import { Modal, Form, Input, InputNumber, message, Select } from 'antd';
import CityAutocomplete from './CityAutocomplete';

const { TextArea } = Input;
const { Option } = Select;

// VAT/Tax number validation based on country
const validateTaxNumber = (_, value, country) => {
  if (!value) {
    return Promise.reject('Please enter tax number');
  }

  const trimmedValue = value.trim();

  if (country === 'Greece' || country === 'Ελλάδα') {
    // Greek AFM: exactly 9 digits
    const greekPattern = /^\d{9}$/;
    if (!greekPattern.test(trimmedValue)) {
      return Promise.reject('Greek tax number (ΑΦΜ) must be exactly 9 digits');
    }
  } else {
    // Other EU countries: 2-letter country code prefix + numbers
    // e.g., DE123456789, FR12345678901
    const euPattern = /^[A-Z]{2}\d{5,12}$/;
    if (!euPattern.test(trimmedValue)) {
      return Promise.reject('Tax number must start with 2-letter country code followed by 5-12 digits (e.g., DE123456789)');
    }
  }

  return Promise.resolve();
};

const PromoteUserModal = ({ visible, user, onClose, onPromote }) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState('Greece');

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);

      await onPromote(user.id, values);
      message.success('User promoted to partner successfully!');
      form.resetFields();
      setSelectedCountry('Greece');
      onClose();
    } catch (error) {
      if (!error.errorFields) {
        // Show detailed error message from backend
        message.error(error.message || 'Failed to promote user');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setSelectedCountry('Greece');
    onClose();
  };

  return (
    <Modal
      title={`Promote User to Partner`}
      open={visible}
      onOk={handleOk}
      onCancel={handleCancel}
      okText="Promote"
      cancelText="Cancel"
      confirmLoading={submitting}
      width={600}
      maskClosable={false}
    >
      {user && (
        <div style={{ marginBottom: 16, padding: 12, background: '#f5f5f5', borderRadius: 4 }}>
          <div><strong>Email:</strong> {user.email}</div>
          <div><strong>Name:</strong> {user.first_name} {user.last_name}</div>
        </div>
      )}

      <Form
        form={form}
        layout="vertical"
        initialValues={{
          platform_fee_percentage: 15,
          country: 'Greece'
        }}
      >
        <Form.Item
          label="Store/Business Name"
          name="store_name"
          rules={[{ required: true, message: 'Please enter store name' }]}
        >
          <Input placeholder="Enter store or business name" />
        </Form.Item>

        <Form.Item
          label="Tax Number / VAT (ΑΦΜ)"
          name="tax_number"
          rules={[
            { required: true, message: 'Please enter tax number' },
            {
              validator: (_, value) => validateTaxNumber(_, value, selectedCountry)
            }
          ]}
          extra={selectedCountry === 'Greece' || selectedCountry === 'Ελλάδα'
            ? 'For Greece: Enter 9-digit ΑΦΜ (e.g., 123456789)'
            : 'For EU countries: Enter country code + digits (e.g., DE123456789)'}
        >
          <Input
            placeholder={selectedCountry === 'Greece' || selectedCountry === 'Ελλάδα'
              ? 'Enter 9-digit ΑΦΜ'
              : 'Enter VAT number (e.g., DE123456789)'}
          />
        </Form.Item>

        <Form.Item
          label="Platform Fee (%)"
          name="platform_fee_percentage"
          rules={[
            { required: true, message: 'Please enter platform fee' },
            { type: 'number', min: 0, max: 100, message: 'Fee must be between 0 and 100' }
          ]}
        >
          <InputNumber
            min={0}
            max={100}
            style={{ width: '100%' }}
            placeholder="Enter platform fee percentage"
          />
        </Form.Item>

        <Form.Item
          label="Address"
          name="address"
          rules={[{ required: true, message: 'Please enter address' }]}
        >
          <TextArea
            rows={2}
            placeholder="Enter full street address"
          />
        </Form.Item>

        <Form.Item
          label="City"
          name="city"
          rules={[{ required: true, message: 'Please enter city' }]}
        >
          <CityAutocomplete placeholder="Enter city" />
        </Form.Item>

        <Form.Item
          label="Postal Code"
          name="postal_code"
        >
          <Input placeholder="Enter postal code (optional)" />
        </Form.Item>

        <Form.Item
          label="Country"
          name="country"
          rules={[{ required: true, message: 'Please select country' }]}
        >
          <Select
            placeholder="Select country"
            onChange={(value) => {
              setSelectedCountry(value);
              // Re-validate tax_number when country changes
              form.validateFields(['tax_number']);
            }}
            showSearch
            optionFilterProp="children"
          >
            <Option value="Greece">Greece (Ελλάδα)</Option>
            <Option value="Cyprus">Cyprus (Κύπρος)</Option>
            <Option value="Austria">Austria (AT)</Option>
            <Option value="Belgium">Belgium (BE)</Option>
            <Option value="Bulgaria">Bulgaria (BG)</Option>
            <Option value="Croatia">Croatia (HR)</Option>
            <Option value="Czech Republic">Czech Republic (CZ)</Option>
            <Option value="Denmark">Denmark (DK)</Option>
            <Option value="Estonia">Estonia (EE)</Option>
            <Option value="Finland">Finland (FI)</Option>
            <Option value="France">France (FR)</Option>
            <Option value="Germany">Germany (DE)</Option>
            <Option value="Hungary">Hungary (HU)</Option>
            <Option value="Ireland">Ireland (IE)</Option>
            <Option value="Italy">Italy (IT)</Option>
            <Option value="Latvia">Latvia (LV)</Option>
            <Option value="Lithuania">Lithuania (LT)</Option>
            <Option value="Luxembourg">Luxembourg (LU)</Option>
            <Option value="Malta">Malta (MT)</Option>
            <Option value="Netherlands">Netherlands (NL)</Option>
            <Option value="Poland">Poland (PL)</Option>
            <Option value="Portugal">Portugal (PT)</Option>
            <Option value="Romania">Romania (RO)</Option>
            <Option value="Slovakia">Slovakia (SK)</Option>
            <Option value="Slovenia">Slovenia (SI)</Option>
            <Option value="Spain">Spain (ES)</Option>
            <Option value="Sweden">Sweden (SE)</Option>
          </Select>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default PromoteUserModal;
